/**
 * MentionInput Component
 *
 * A textarea component that supports @mentions and #channel mentions
 * with autocomplete suggestions.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Fuse from 'fuse.js';
import api from '../../services/api';

export interface Mentionable {
  id: string;
  type: 'user' | 'agent' | 'channel';
  name?: string;
  username?: string;
  display_name: string;
  description?: string;
  agent_type?: string;
  avatar_url?: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange?: (mentions: { userIds: string[]; agentIds: string[]; channelIds: string[] }) => void;
  projectId: string;
  workspaceId: string;
  placeholder?: string;
  className?: string;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onSubmit?: () => void;
}

interface MentionSuggestion {
  mentionable: Mentionable;
  matchText: string;
}

export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  onMentionsChange,
  projectId,
  workspaceId,
  placeholder = 'Type a message... Use @ to mention, # for channels',
  className = '',
  minHeight = 80,
  maxHeight = 300,
  disabled = false,
  onKeyDown,
  onSubmit,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const [mentionables, setMentionables] = useState<Mentionable[]>([]);
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionTrigger, setMentionTrigger] = useState<{ type: '@' | '#'; start: number; query: string } | null>(null);

  // Fuse.js instance for fuzzy search
  const fuseRef = useRef<Fuse<Mentionable> | null>(null);

  // Load mentionables on mount
  useEffect(() => {
    const loadMentionables = async () => {
      try {
        const response = await api.get(`/inbox/projects/${projectId}/mentionables`, {
          params: { workspace_id: workspaceId, limit: 50 }
        });
        setMentionables(response.data);

        // Initialize Fuse.js
        fuseRef.current = new Fuse(response.data, {
          keys: ['display_name', 'username', 'name'],
          threshold: 0.3,
          includeScore: true,
        });
      } catch (error) {
        console.error('Failed to load mentionables:', error);
      }
    };

    if (projectId && workspaceId) {
      loadMentionables();
    }
  }, [projectId, workspaceId]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
    }
  }, [value, minHeight, maxHeight]);

  // Extract mentions from current value
  const extractMentions = useCallback((text: string) => {
    const userIds: string[] = [];
    const agentIds: string[] = [];
    const channelIds: string[] = [];

    // Pattern for @mentions and #channels in the text
    const mentionPattern = /(@|#)(\w+(?:\s+\w+)*)/g;
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
      const trigger = match[1];
      const name = match[2];

      const mentionable = mentionables.find(m => {
        if (trigger === '#') return m.type === 'channel' && m.display_name.toLowerCase() === name.toLowerCase();
        return (m.type === 'user' || m.type === 'agent') &&
               (m.display_name.toLowerCase() === name.toLowerCase() ||
                m.username?.toLowerCase() === name.toLowerCase());
      });

      if (mentionable) {
        if (mentionable.type === 'user') userIds.push(mentionable.id);
        else if (mentionable.type === 'agent') agentIds.push(mentionable.id);
        else if (mentionable.type === 'channel') channelIds.push(mentionable.id);
      }
    }

    return { userIds, agentIds, channelIds };
  }, [mentionables]);

  // Detect mention trigger at cursor position
  const detectMentionTrigger = useCallback((text: string, cursorPos: number) => {
    // Look backwards from cursor to find @ or #
    const beforeCursor = text.slice(0, cursorPos);

    // Find the last @ or # that starts a potential mention
    const atMatch = beforeCursor.match(/@([^\s@#]*)$/);
    const hashMatch = beforeCursor.match(/#([^\s@#]*)$/);

    if (atMatch) {
      return {
        type: '@' as const,
        start: cursorPos - atMatch[0].length,
        query: atMatch[1]
      };
    }

    if (hashMatch) {
      return {
        type: '#' as const,
        start: cursorPos - hashMatch[0].length,
        query: hashMatch[1]
      };
    }

    return null;
  }, []);

  // Update suggestions based on trigger
  const updateSuggestions = useCallback((trigger: { type: '@' | '#'; query: string } | null) => {
    if (!trigger || !fuseRef.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Filter by type first
    let filtered = mentionables;
    if (trigger.type === '#') {
      filtered = mentionables.filter(m => m.type === 'channel');
    } else {
      filtered = mentionables.filter(m => m.type === 'user' || m.type === 'agent');
    }

    // If there's a query, use fuzzy search
    if (trigger.query) {
      const fuse = new Fuse(filtered, {
        keys: ['display_name', 'username', 'name'],
        threshold: 0.3,
      });
      const results = fuse.search(trigger.query).slice(0, 8);
      setSuggestions(results.map(r => ({
        mentionable: r.item,
        matchText: trigger.query
      })));
    } else {
      // Show first 8 items
      setSuggestions(filtered.slice(0, 8).map(m => ({
        mentionable: m,
        matchText: ''
      })));
    }

    setShowSuggestions(true);
    setSelectedIndex(0);
  }, [mentionables]);


  // Handle text change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    onChange(newValue);

    // Detect mention trigger
    const trigger = detectMentionTrigger(newValue, cursorPos);
    setMentionTrigger(trigger);
    updateSuggestions(trigger);

    // Extract and report mentions
    if (onMentionsChange) {
      const mentions = extractMentions(newValue);
      onMentionsChange(mentions);
    }
  };

  // Handle selecting a suggestion
  const selectSuggestion = (suggestion: MentionSuggestion) => {
    if (!mentionTrigger || !textareaRef.current) return;

    const { mentionable } = suggestion;
    const displayText = mentionable.type === 'channel'
      ? `#${mentionable.display_name}`
      : `@${mentionable.display_name}`;

    // Replace the trigger text with the mention
    const before = value.slice(0, mentionTrigger.start);
    const after = value.slice(textareaRef.current.selectionStart);
    const newValue = `${before}${displayText} ${after}`;

    onChange(newValue);
    setShowSuggestions(false);
    setMentionTrigger(null);

    // Report mentions
    if (onMentionsChange) {
      const mentions = extractMentions(newValue);
      onMentionsChange(mentions);
    }

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = before.length + displayText.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % suggestions.length);
          return;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
          return;
        case 'Tab':
        case 'Enter':
          if (suggestions[selectedIndex]) {
            e.preventDefault();
            selectSuggestion(suggestions[selectedIndex]);
            return;
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          return;
      }
    }

    // Handle Cmd/Ctrl + Enter to submit
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit?.();
      return;
    }

    onKeyDown?.(e);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600
          bg-white dark:bg-gray-800 px-4 py-3 text-gray-900 dark:text-gray-100
          focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none
          disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
          ${className}`}
        style={{ minHeight, maxHeight }}
      />

      {/* Suggestions Popup */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{
            top: '100%',
            left: 0,
            marginTop: 4,
          }}
        >
          <div className="py-1 max-h-64 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.mentionable.id}
                type="button"
                className={`w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700
                  ${index === selectedIndex ? 'bg-blue-500/10 dark:bg-blue-900/30' : ''}`}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {/* Avatar/Icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium
                  ${suggestion.mentionable.type === 'user' ? 'bg-blue-500' :
                    suggestion.mentionable.type === 'agent' ? 'bg-purple-500' : 'bg-gray-500'}`}>
                  {suggestion.mentionable.type === 'channel' ? '#' :
                    suggestion.mentionable.display_name.charAt(0).toUpperCase()}
                </div>

                {/* Name and type */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {suggestion.mentionable.type === 'channel' ? '#' : '@'}
                    {suggestion.mentionable.display_name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {suggestion.mentionable.type}
                    {suggestion.mentionable.agent_type && ` - ${suggestion.mentionable.agent_type}`}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MentionInput;
